import React from 'react';
import { ScrollView, View, Image } from 'react-native';
import styled from 'styled-components/native';
import { useRoute, RouteProp } from '@react-navigation/native';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { useTheme } from '@theme/ThemeProvider';
import { Text, Card, ProgressBar } from '@ui';
import { useJourney } from '@api/hooks';
import { JourneyTopTabParamList } from '@navigation/types';

const Container = styled.View`
  flex: 1;
  background-color: ${({ theme }) => theme.colors.background};
`;

const Content = styled(ScrollView)`
  flex: 1;
  padding: ${({ theme }) => theme.spacing.base}px;
`;

const ProgressSection = styled(Card)`
  margin-bottom: ${({ theme }) => theme.spacing.base}px;
`;

const InsightCard = styled(Card)`
  flex-direction: row;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.md}px;
`;

const IconWrapper = styled.View<{ variant: 'success' | 'warning' | 'info' }>`
  width: 48px;
  height: 48px;
  border-radius: ${({ theme }) => theme.radius.md}px;
  background-color: ${({ theme, variant }) => {
    switch (variant) {
      case 'success':
        return theme.colors.successLight;
      case 'warning':
        return theme.colors.warningLight;
      default:
        return theme.colors.infoLight;
    }
  }};
  align-items: center;
  justify-content: center;
  margin-right: ${({ theme }) => theme.spacing.md}px;
`;

const InfoContent = styled.View`
  flex: 1;
`;

const HeroImage = styled(Image)`
  width: 100%;
  height: 200px;
  border-radius: ${({ theme }) => theme.radius.md}px;
  margin-bottom: ${({ theme }) => theme.spacing.base}px;
`;

const TipImage = styled(Image)`
  width: 100%;
  height: 160px;
  border-radius: ${({ theme }) => theme.radius.md}px;
  margin-bottom: ${({ theme }) => theme.spacing.md}px;
`;

type OverviewRouteProp = RouteProp<JourneyTopTabParamList, 'Overview'>;

const JourneyOverviewTab: React.FC = () => {
  const theme = useTheme();
  const route = useRoute<OverviewRouteProp>();
  const { journeyId } = route.params;
  const { data: journey } = useJourney(journeyId);

  if (!journey) {
    return (
      <Container>
        <Content>
          <Text variant="body" color="textSecondary">
            Loading...
          </Text>
        </Content>
      </Container>
    );
  }

  return (
    <Container>
      <Content showsVerticalScrollIndicator={false}>
        {/* Hero Image */}
        <HeroImage
          source={{
            uri: 'https://images.unsplash.com/photo-1555252333-9f8e92e65df9?w=800&q=80',
          }}
          resizeMode="cover"
        />

        {/* Progress Section */}
        <ProgressSection padding="md">
          <Text variant="titleMedium" color="textPrimary">
            Your Journey Progress
          </Text>
          <View style={{ marginVertical: 12 }}>
            <ProgressBar progress={journey.progress} height={8} animated />
          </View>
          <Text variant="bodyMedium" color="textSecondary">
            {journey.progress}% Complete • Keep up the great work!
          </Text>
        </ProgressSection>

        {/* Key Milestones */}
        <Text variant="title" color="textPrimary" style={{ marginBottom: 12 }}>
          Key Milestones
        </Text>

        <InsightCard padding="md">
          <IconWrapper variant="success">
            <Icon name="check-circle" size={24} color={theme.colors.success} />
          </IconWrapper>
          <InfoContent>
            <Text variant="titleMedium" color="textPrimary">
              First Trimester Completed
            </Text>
            <Text variant="bodySmall" color="textSecondary" style={{ marginTop: 4 }}>
              You've completed all essential first trimester checkups
            </Text>
          </InfoContent>
        </InsightCard>

        <InsightCard padding="md">
          <IconWrapper variant="warning">
            <Icon name="schedule" size={24} color={theme.colors.warning} />
          </IconWrapper>
          <InfoContent>
            <Text variant="titleMedium" color="textPrimary">
              Upcoming: 12-Week Scan
            </Text>
            <Text variant="bodySmall" color="textSecondary" style={{ marginTop: 4 }}>
              Schedule your ultrasound appointment soon
            </Text>
          </InfoContent>
        </InsightCard>

        <InsightCard padding="md">
          <IconWrapper variant="info">
            <Icon name="info" size={24} color={theme.colors.info} />
          </IconWrapper>
          <InfoContent>
            <Text variant="titleMedium" color="textPrimary">
              Maternity Coverage Active
            </Text>
            <Text variant="bodySmall" color="textSecondary" style={{ marginTop: 4 }}>
              Your AIA maternity benefits are fully active
            </Text>
          </InfoContent>
        </InsightCard>

        {/* Personalized Tips */}
        <Text variant="title" color="textPrimary" style={{ marginTop: 12, marginBottom: 12 }}>
          Personalized Tips
        </Text>

        <Card padding="none" style={{ marginBottom: 12 }}>
          <TipImage
            source={{
              uri: 'https://images.unsplash.com/photo-1559827260-dc66d52bef19?w=800&q=80',
            }}
            resizeMode="cover"
          />
          <View style={{ padding: 16 }}>
            <Text variant="titleMedium" color="textPrimary" style={{ marginBottom: 8 }}>
              Stay Hydrated
            </Text>
            <Text variant="bodyMedium" color="textSecondary">
              Aim for 8-10 glasses of water daily during pregnancy to support your body's increased
              needs.
            </Text>
          </View>
        </Card>

        <Card padding="md" style={{ marginBottom: 12 }}>
          <Text variant="titleMedium" color="textPrimary" style={{ marginBottom: 8 }}>
            Prenatal Vitamins
          </Text>
          <Text variant="bodyMedium" color="textSecondary">
            Continue taking your prenatal vitamins with folic acid daily for optimal fetal
            development.
          </Text>
        </Card>

        <Card padding="none" style={{ marginBottom: 12 }}>
          <TipImage
            source={{
              uri: 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=800&q=80',
            }}
            resizeMode="cover"
          />
          <View style={{ padding: 16 }}>
            <Text variant="titleMedium" color="textPrimary" style={{ marginBottom: 8 }}>
              Gentle Exercise
            </Text>
            <Text variant="bodyMedium" color="textSecondary">
              Consider prenatal yoga or swimming. Check out our wellness programs for classes near
              you.
            </Text>
          </View>
        </Card>

        <View style={{ height: 24 }} />
      </Content>
    </Container>
  );
};

export default JourneyOverviewTab;
